import React, { useMemo } from "react";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  useAnimatedReaction,
  SharedValue,
} from "react-native-reanimated";

import { VStack } from "@whoosh/design-system/src/components/primitive";

import {
  MobileDateSwitcher,
  MobileDateSwitcherProps,
} from "../MobileDateSwitcher/MobileDateSwitcher";
import {
  MobileFiltersBar,
  MobileFiltersBarProps,
} from "../MobileFiltersBar/MobileFiltersBar";

export { getClampedScrollOffsetY } from "./getClampedScrollOffsetY";

const NAVIGATION_BAR_HEIGHTS = {
  default: 56,
  topbar: 72,
} as const;

export type NavigationBarVariant = keyof typeof NAVIGATION_BAR_HEIGHTS;

export type MobileHeaderProps = {
  customNavigationBar?: React.ReactNode;
  navigationBarVariant?: NavigationBarVariant;
  filtersBar?: MobileFiltersBarProps;
  dateSwitcher?: MobileDateSwitcherProps;
  /**
   * Scroll position from external scroll container.
   * Component will track scroll direction and hide/show navigation bar.
   * Pass a shared value that updates on scroll events.
   */
  scrollY?: SharedValue<number>;
};

export const MobileHeader = ({
  customNavigationBar,
  navigationBarVariant = "default",
  filtersBar,
  dateSwitcher,
  scrollY,
}: MobileHeaderProps) => {
  const navigationBarHeight = NAVIGATION_BAR_HEIGHTS[navigationBarVariant];
  const lastScrollY = useSharedValue(0);
  const rawAccumulatedScroll = useSharedValue(0);

  const filtersBarValuesString = useMemo(
    () => JSON.stringify(filtersBar?.values),
    [filtersBar?.values]
  );

  const memoizedFiltersBarProps = useMemo(
    () => filtersBar,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filtersBar?.onPress,
      filtersBar?.title.leadingText,
      filtersBar?.title.boldText,
      filtersBar?.title.trailingText,
      filtersBarValuesString,
      filtersBar?.rightAction,
      filtersBar?.onRightActionClick,
    ]
  );

  const memoizedDateSwitcherProps = useMemo(
    () => dateSwitcher,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dateSwitcher?.startDate,
      dateSwitcher?.endDate,
      dateSwitcher?.selectedDate,
      dateSwitcher?.onChange,
      dateSwitcher?.clubWeekStartsOn,
    ]
  );

  // Handle scroll position from shared value - move nav bar in sync with scroll
  useAnimatedReaction(
    () => (scrollY ? scrollY.value : 0),
    (currentY, previousY) => {
      const normalizedCurrentY = Math.max(0, currentY);
      if (previousY === null) {
        lastScrollY.value = normalizedCurrentY;
        rawAccumulatedScroll.value = 0;
        return;
      }

      const deltaY = normalizedCurrentY - lastScrollY.value;

      // Accumulate scroll delta
      // Normalize scrollY to avoid negative iOS overscroll values creating
      // delta desync. Keep accumulator bounded so any upward scroll reveals
      // the header immediately.
      rawAccumulatedScroll.value = Math.max(
        0,
        Math.min(navigationBarHeight, rawAccumulatedScroll.value + deltaY)
      );

      lastScrollY.value = normalizedCurrentY;
    },
    [scrollY]
  );

  // Animated style for navigation bar container (height and overflow)
  // Height decreases as clamped scroll increases.
  const animatedNavBarContainerStyle = useAnimatedStyle(() => {
    const clampedScroll = Math.max(
      0,
      Math.min(navigationBarHeight, rawAccumulatedScroll.value)
    );
    const height = navigationBarHeight - clampedScroll;
    return {
      height: Math.max(0, height),
      overflow: "hidden" as const,
    };
  });

  // Animated style for navigation bar (translate)
  // Translate up by the accumulated scroll amount
  const animatedNavBarStyle = useAnimatedStyle(() => {
    const clampedScroll = Math.max(
      0,
      Math.min(navigationBarHeight, rawAccumulatedScroll.value)
    );
    return {
      transform: [{ translateY: -clampedScroll }],
    };
  });

  // Only animate if scroll tracking is provided
  const shouldAnimate = scrollY !== undefined;

  return (
    <VStack
      paddingHorizontal={3}
      paddingTop={2}
      paddingBottom={5}
      gap={3}
      backgroundColor="surface-primary"
      style={{
        borderBottomLeftRadius: 20,
        borderBottomRightRadius: 20,
        overflow: "hidden",
      }}
    >
      {customNavigationBar ? (
        shouldAnimate ? (
          <Animated.View style={animatedNavBarContainerStyle}>
            <Animated.View style={animatedNavBarStyle}>
              {customNavigationBar}
            </Animated.View>
          </Animated.View>
        ) : (
          customNavigationBar
        )
      ) : null}

      {filtersBar && memoizedFiltersBarProps && (
        <MobileFiltersBar {...memoizedFiltersBarProps} />
      )}

      {dateSwitcher && memoizedDateSwitcherProps && (
        <MobileDateSwitcher {...memoizedDateSwitcherProps} />
      )}
    </VStack>
  );
};
